import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  ActiveQuestionMetaType,
  PreAssessmentData
} from "../../types/preAssessment";
import {
  AssessmentData,
  QuestionProgressStatus,
  SectionProgressStatus,
  SectionTimeSpent
} from "../../types/assessmentOngoing";
import { AccessStatus } from "../../types/accessStatus";
import { ScheduleType } from "@assessment/types/schedulerType";

interface INIT {
  accessDetails: {
    allowedAfter?: string;
    accessAllowed: boolean;
    accessStatus: AccessStatus;
    ongoingQpDuration: number;
    ongoingId?: string;
    attempts?: {
      completed: number;
      maxAttempts: number;
      inProgress: number;
    };
    coolofftime: number;
    title?: string;
    scheduleType?: ScheduleType;
    scheduleStartTime?: string;
    scheduleEndTime?: string;
    qpDuration?: number;
    assessmentOngoingStartedAt?: string;
    vpEnabled?: boolean;
    bpEnabled?: boolean;
    sessionId?: string;
  };
  activeQuestionMeta: ActiveQuestionMetaType | null;
  applyReviewIndicationStyling: boolean;
  assessmentActiveView: string;
  assessmentData: AssessmentData | undefined;
  assessmentHasStarted: boolean;
  assessmentProgress: {
    [sectionId: string]: SectionProgressStatus;
  };
  candidateDetails: {
    email: string;
    firstName: string;
    lastName: string;
  };
  candidateResponse: Record<string, undefined>;
  candidateFibResponse: Record<string, undefined>;
  expandAll: boolean;
  initialProctoringVideoPreview: boolean;
  modalFlags: {
    showSubmissionsModal: boolean;
    showSummaryModal: boolean;
    showWarningModal: boolean;
  };
  ongoingId: string | undefined;
  preAssessment: {
    stepperFormActiveStep: number;
  };
  preAssessmentData: PreAssessmentData;
  proctoring: {
    devices: {
      camera: string;
    };
    showProctoringVideoPreview: boolean;
  };
  shuffledOptionsForObjectiveQuestions: {
    [questionId: string]: Record<string, string>;
  };
  timeSpentDetails: {
    [sectionId: string]: SectionTimeSpent;
  };
  videoPreviewLoader: boolean;
  testCases: Record<string, undefined>;
  mode: boolean;
  timesUpModalOpen: boolean;
  questionType: string;
  timeLeft: number;
  isOnline: boolean;
  duration: {
    idle: number;
    autoSave: number;
    frameRateInSec: number;
    practiceDurationInSec: number;
    mediaCheckDurationInSec: number;
    heartBeatInterval: number;
  };
  latestCodingState: {
    questionId: string;
    code: string;
    language: string;
    version: number;
  } | null;
  testCaseResultHistory: Record<string, Record<string, unknown>>;
  isSaving: boolean;
  visitorId: any;
  editorFullScreen: boolean;
  starterCodeList: any;
}

const INIT_STATE: INIT = {
  accessDetails: {
    allowedAfter: undefined,
    accessAllowed: false,
    accessStatus: AccessStatus.SCHEDULE_NOT_STARTED,
    coolofftime: 0,
    ongoingQpDuration: 0,
    scheduleType: undefined,
    scheduleStartTime: undefined,
    scheduleEndTime: undefined,
    qpDuration: undefined,
    assessmentOngoingStartedAt: undefined,
    vpEnabled: false,
    bpEnabled: false,
    sessionId: undefined
  },
  activeQuestionMeta: null,
  applyReviewIndicationStyling: false,
  assessmentActiveView: "dashboard",
  assessmentData: undefined,
  assessmentHasStarted: false,
  assessmentProgress: {},
  candidateDetails: {
    email: "",
    firstName: "",
    lastName: ""
  },
  candidateResponse: {},
  candidateFibResponse: {},
  expandAll: false,
  initialProctoringVideoPreview: false,
  modalFlags: {
    showSubmissionsModal: false,
    showSummaryModal: false,
    showWarningModal: false
  },
  ongoingId: undefined,
  preAssessment: {
    stepperFormActiveStep: 1
  },
  preAssessmentData: {} as PreAssessmentData,
  proctoring: {
    devices: { camera: "" },
    showProctoringVideoPreview: false
  },
  shuffledOptionsForObjectiveQuestions: {},
  timeSpentDetails: {},
  videoPreviewLoader: false,
  testCases: {},
  mode: false,
  timesUpModalOpen: false,
  questionType: "",
  timeLeft: 100,
  isOnline: true,
  duration: {
    idle: 180,
    autoSave: 120,
    frameRateInSec: 40,
    practiceDurationInSec: 30,
    mediaCheckDurationInSec: 10,
    heartBeatInterval: 30
  },
  latestCodingState: null,
  testCaseResultHistory: {},
  isSaving: false,
  visitorId: null,
  editorFullScreen: false,
  starterCodeList: {}
};

const AssessmentSlice = createSlice({
  name: "AssessmentSlice",
  initialState: INIT_STATE,
  reducers: {
    mutateAccessDetails: (state, action) => {
      state.accessDetails = action.payload;
    },
    mutateAssessmentData: (state, action) => {
      state.assessmentData = action.payload;
    },
    mutateAssessmentHasStarted: (state, action) => {
      state.assessmentHasStarted = action.payload;
    },
    mutateShowWarningModal: (state, action) => {
      state.modalFlags.showWarningModal = action.payload;
    },
    mutateShowSubmissionModal: (state, action) => {
      state.modalFlags.showSubmissionsModal = action.payload;
    },
    mutateShowSummaryModal: (state, action) => {
      state.modalFlags.showSummaryModal = action.payload;
    },
    mutateAssessmentView: (state, action) => {
      state.assessmentActiveView = action.payload;
    },
    mutateActiveQuestionMeta: (state, action) => {
      state.activeQuestionMeta = action.payload;
    },
    mutateCandidateDetails: (state, action) => {
      state.candidateDetails = action.payload;
    },
    mutateCandidateResponse: (state, action) => {
      state.candidateResponse[action.payload.questionId] =
        action.payload.answerKey;
    },
    mutateCandidateResponseBulk: (state, action) => {
      state.candidateResponse = action.payload;
    },
    mutateCandidateFibResponse: (state, action) => {
      state.candidateFibResponse[action.payload.questionId] =
        action.payload.answerKey;
    },
    mutateCandidateFibResponseBulk: (state, action) => {
      state.candidateFibResponse = action.payload;
    },
    mutateReviewIndicationStyling: (state, action) => {
      state.applyReviewIndicationStyling = action.payload;
    },
    mutateMediaDevices: (state, action) => {
      state.proctoring.devices.camera = action.payload;
    },
    mutateShowProctoringVideoPreview: (state, action) => {
      state.proctoring.showProctoringVideoPreview = action.payload;
    },
    mutateStepperFormActiveStep: (state, action) => {
      state.preAssessment.stepperFormActiveStep = action.payload;
    },
    mutatePreAssessmentData: (state, action) => {
      state.preAssessmentData = action.payload;
    },
    mutateOnGoingId: (state, action) => {
      console.log("Ongoing id", action.payload);
      state.ongoingId = action.payload;
    },
    mutateAssessmentProgress: (state, action) => {
      state.assessmentProgress = action.payload;
    },
    mutateSectionProgress: (
      state,
      action: PayloadAction<{
        sectionId: string;
        status: SectionProgressStatus["status"];
      }>
    ) => {
      const { sectionId, status } = action.payload;
      const section = state.assessmentProgress[sectionId];
      if (section) {
        section.status = status;
      }
    },
    mutateQuestionProgress: (
      state,
      action: PayloadAction<{
        sectionId: string | undefined;
        questionId: string;
        status: QuestionProgressStatus["status"];
      }>
    ) => {
      const { sectionId, questionId, status } = action.payload;
      const section = state.assessmentProgress[sectionId as string];
      if (section && section.questions[questionId]) {
        section.questions[questionId].status = status;
      }
    },
    mutateQuestionFlagged: (
      state,
      action: PayloadAction<{
        sectionId: string;
        questionId: string;
        value: boolean;
      }>
    ) => {
      const { sectionId, questionId, value } = action.payload;
      const section = state.assessmentProgress[sectionId];
      if (section && section.questions[questionId]) {
        section.questions[questionId].flagged = value;
      }
    },
    mutateTimeSpentBulk: (state, action) => {
      state.timeSpentDetails = action.payload;
    },
    mutateTimeSpentOnSection: (
      state,
      action: PayloadAction<{ sectionId: string; value: number }>
    ) => {
      const { sectionId, value } = action.payload;
      const section = state.timeSpentDetails[sectionId];
      if (section) {
        section.value = value;
      }
    },
    mutateTimeSpentOnQuestion: (
      state,
      action: PayloadAction<{
        sectionId: string;
        questionId: string;
        value: number;
      }>
    ) => {
      const { sectionId, questionId, value } = action.payload;
      const section = state.timeSpentDetails[sectionId];
      if (section && section.questions[questionId]) {
        section.questions[questionId].value = value;
      }
    },
    mutateShuffledOptionsForObjectiveQuestions: (state, action) => {
      state.shuffledOptionsForObjectiveQuestions = action.payload;
    },
    mutateExpandAll: (state, action) => {
      state.expandAll = action.payload;
    },
    mutateInitialProctoringVideoPreview: (state, action) => {
      state.initialProctoringVideoPreview = action.payload;
    },
    mutateVideoPreviewLoader: (state, action) => {
      state.videoPreviewLoader = action.payload;
    },
    mutateTestCases: (state, action) => {
      state.testCases = action.payload;
    },
    mutateTheme: (state, action) => {
      state.mode = action.payload;
    },
    mutateTimesUpModalOpen: (state, action) => {
      state.timesUpModalOpen = action.payload;
    },
    mutateQuestionType: (state, action) => {
      state.questionType = action.payload;
    },
    mutateTimeLeft: (state, action) => {
      state.timeLeft = action.payload;
    },
    mutateOnline: (state, action) => {
      state.isOnline = action.payload;
    },
    mutateConstants: (state, action) => {
      state.duration = action.payload;
    },
    mutateSaving: (state, action) => {
      state.isSaving = action.payload;
    },
    mutateLatestCodingState: (state, action) => {
      state.latestCodingState = action.payload;
    },
    mutateFullScreen: (state, action) => {
      state.editorFullScreen = action.payload;
    },
    mutateStarterCodeList: (state, action) => {
      state.starterCodeList = action.payload;
    },
    mutateTestCaseResultHistory: (
      state,
      action: PayloadAction<{
        questionId: string;
        testCaseId: string;
        result: Record<string, unknown>;
        testCaseType: string;
      }>
    ) => {
      const { questionId, testCaseId, result, testCaseType } = action.payload;

      const isEmptyResult = Object.keys(result).length === 0;

      if (testCaseId === "" && isEmptyResult) {
        state.testCaseResultHistory[questionId] = {};
        return;
      }

      if (!state.testCaseResultHistory[questionId]) {
        state.testCaseResultHistory[questionId] = {};
      }

      state.testCaseResultHistory[questionId][testCaseId] = {
        ...result,
        testCaseType
      };
    },
    mutateVisitorId: (state, action) => {
      state.visitorId = action.payload;
    },
    resetState: () => {
      return INIT_STATE;
    }
  }
});

export default AssessmentSlice;

export const AssessmentSliceActions = AssessmentSlice.actions;
